/**
 * nodeCatalog — every node kind the editor can draw and configure.
 *
 * Each entry: { kind, displayName, category, icon, description, defaults,
 * configSchema, validate? }. `description` is shown on hover only; the
 * canvas stays clean; the picker shows it as the row's one-line subtitle.
 * `configSchema` drives the settings form (see NodeConfigFields);
 * `validate(config)` says what is missing and, via `field`, where.
 *
 * Categories: 'trigger' | 'ai' | 'data' | 'flow' | 'integration'.
 *
 * Branch ports: `ports` for a fixed list (If: true / false), or
 * `portsOf(config)` + `portLabel(config, port)` when the node's own config
 * names them (Decide: one per route, plus Otherwise). Read them through
 * `portsOf(node)` / `portLabel(node, port)` below, never `spec.ports`.
 *
 * Gone in v2 and refused by the server: the live-event and pattern triggers
 * (Alerts do this), the model / tools sub-nodes (an AI Agent carries its
 * model and tools in its own config), the standalone MCP node (MCP lives
 * inside an agent's tools), and every Jira and Salesforce node.
 *
 * An AI node runs on one of the team's own model integrations; the Trodo Ask
 * node runs on whatever the team set in Ask setup.
 */

import {
  Hand, CalendarClock, Webhook, Bell,
  Database, Code2, GitBranch, RefreshCw, Square, Pause, Hammer,
  Bot, Send, FunctionSquare, Filter as FilterIcon,
  Slack, Github, Box, Sparkles, Wrench, Split,
} from 'lucide-react';
import { fromApi, rowProblems } from 'shared/decision/questions';
import { tableProblems } from 'shared/decision/routing';
import { ALL_INTEGRATION_NODES, PALETTE_PROVIDERS, actionGroupsFor } from 'features/agents/WorkflowEditor/integrationsCatalog';
import { detectedTimezone } from 'features/agents/WorkflowEditor/scheduleCron';

const need = (v) => v !== undefined && v !== null && String(v).trim() !== '';
/** `field` names the config key the message belongs under, so the form can put it there. */
const fail = (message, field) => ({ ok: false, message, field });
const ok = { ok: true };

/**
 * Model selection, shared by the AI nodes. An AI node always runs on one of
 * the team's own model integrations: the call goes out on their key and costs
 * no credits. Trodo's own models are for Ask, which follows the team's Ask
 * setup and is metered there.
 */
const MODEL_FIELD = { key: 'model', label: 'Model', type: 'agent_model' };
const validateModel = (m) => {
  if (!need(m?.credential_id)) return fail('Choose the provider this runs on.', 'model');
  if (!need(m?.model)) return fail('Choose the model.', 'model');
  return null;
};

/** A Decide node's ports: its route ids in order, then 'otherwise' when on. */
function decidePorts(c) {
  const table = c && c.routes && typeof c.routes === 'object' ? c.routes : {};
  const ids = (Array.isArray(table.routes) ? table.routes : []).map((r) => String(r?.id || '')).filter(Boolean);
  if (table.otherwise) ids.push('otherwise');
  return ids;
}

function decidePortLabel(c, port) {
  const table = (c && c.routes) || {};
  if (port === 'otherwise') return String(table.otherwise?.name || '').trim() || 'Otherwise';
  const r = (table.routes || []).find((x) => x && x.id === port);
  return String(r?.name || '').trim() || port;
}

export const NODE_CATALOG = {
  // ─── Triggers ────────────────────────────────────────────────────────────
  trigger_manual: {
    kind: 'trigger_manual', displayName: 'Manual', category: 'trigger', icon: Hand,
    description: 'Starts when you press Run.',
    defaults: {}, configSchema: [],
  },
  trigger_schedule: {
    kind: 'trigger_schedule', displayName: 'Schedule', category: 'trigger', icon: CalendarClock,
    description: 'Starts on a schedule, in a timezone you choose.',
    defaults: () => ({ cron: '0 9 * * *', timezone: detectedTimezone() }),
    configSchema: [
      { key: 'cron', label: 'When it runs', type: 'schedule' },
    ],
    validate: (c) => (need(c.cron) ? ok : fail('Set when this runs.', 'cron')),
  },
  trigger_webhook: {
    kind: 'trigger_webhook', displayName: 'Webhook', category: 'trigger', icon: Webhook,
    description: 'Starts when something POSTs to the agent\'s URL.',
    defaults: { require_hmac: false },
    configSchema: [
      { key: 'require_hmac', label: 'Require a signature', type: 'checkbox' },
      { key: 'hmac_header', label: 'Signature header', type: 'text', placeholder: 'X-Hub-Signature-256', showIf: (c) => !!c.require_hmac },
    ],
  },
  trigger_alert: {
    kind: 'trigger_alert', displayName: 'Alert', category: 'trigger', icon: Bell,
    description: 'Starts when a chosen alert fires.',
    defaults: { alert_ids: [] },
    // `alert_ids` since TRO-498; agents published earlier carry one `alert_id`,
    // which the picker and the server both still read.
    configSchema: [
      { key: 'alert_ids', label: 'Alerts', type: 'alert_multi_picker' },
    ],
    validate: (c) => ((Array.isArray(c.alert_ids) && c.alert_ids.length) || need(c.alert_id)
      ? ok : fail('Choose at least one alert that starts this agent.', 'alert_ids')),
  },

  // ─── AI ──────────────────────────────────────────────────────────────────
  ai_agent: {
    kind: 'ai_agent', displayName: 'AI Agent', category: 'ai', icon: Bot,
    description: 'A model that calls tools until it has an answer.',
    defaults: () => ({
      system_prompt: 'You are a helpful assistant.',
      user_message: '{{ $input }}',
      max_iters: 8,
      return_intermediate_steps: false,
      model: {},
      tools: [],
    }),
    configSchema: [
      { key: 'user_message', label: 'Message', type: 'code', language: 'markdown' },
      { key: 'system_prompt', label: 'Instructions', type: 'code', language: 'markdown' },
      MODEL_FIELD,
      { key: 'tools', label: 'Tools', type: 'agent_tools' },
      // Not "parameters to add": both always have a meaningful default, so
      // they render as ordinary fields rather than behind an add/remove row.
      // The generation knobs (temperature and the rest) live in Params, on
      // the model row, where an unset one really does mean "provider default".
      { key: 'max_iters', label: 'Max turns', type: 'number' },
      { key: 'return_intermediate_steps', label: 'Return tool calls in the output', type: 'checkbox' },
    ],
    validate: (c) => validateModel(c.model) || ok,
  },
  llm_call: {
    kind: 'llm_call', displayName: 'LLM', category: 'ai', icon: Sparkles,
    description: 'One model call, no tools. Prompt in, text out.',
    defaults: () => ({ system_prompt: '', user_prompt: '{{ $input }}', model: {} }),
    configSchema: [
      { key: 'user_prompt', label: 'Prompt', type: 'code', language: 'markdown' },
      { key: 'system_prompt', label: 'Instructions', type: 'code', language: 'markdown' },
      MODEL_FIELD,
      { key: 'for_each', label: 'For each (optional)', type: 'expression', placeholder: '{{ $json.rows }}', help: 'Run the prompt once per item of this list. The item is {{ $item }}, its position {{ $index }}. One execution per item.' },
      { key: 'concurrency', label: 'At a time', type: 'number', placeholder: '4', showIf: (c) => !!String(c.for_each || '').trim() },
      { key: 'max_items', label: 'Items at most', type: 'number', placeholder: '500', showIf: (c) => !!String(c.for_each || '').trim() },
    ],
    validate: (c) => validateModel(c.model) || ok,
  },
  hand_to_coding_agent: {
    kind: 'hand_to_coding_agent', displayName: 'Hand to coding agent', category: 'ai', icon: Wrench,
    description: 'Give an issue to Cursor or Claude Code to fix on a branch.',
    defaults: { issue_id: '', agent: 'auto', instructions: '' },
    configSchema: [
      { key: 'issue_id', label: 'Issue', help: 'Empty uses the issue the previous step filed.', type: 'expression', placeholder: '{{ $json.issue.id }}' },
      { key: 'agent', label: 'Coding agent', type: 'select', options: [
        { value: 'auto', label: 'Whichever is connected' },
        { value: 'claudecode', label: 'Claude Code' },
        { value: 'cursor', label: 'Cursor' },
      ] },
      { key: 'instructions', label: 'Instructions (optional)', type: 'code', language: 'markdown' },
    ],
    validate: () => ok,
  },

  trodo_ask: {
    kind: 'trodo_ask', displayName: 'Lucid', category: 'ai', logo: '/ask/ask-ember.svg',
    description: 'Ask Lucid a question, investigate an alert, or improve an eval.',
    defaults: { task: 'answer', question: '', mode: 'quick', effort: 'medium' },
    configSchema: [
      { key: 'task', label: 'Task', type: 'select', options: [
        { value: 'answer', label: 'Answer a question' },
        { value: 'investigate_issue', label: 'Investigate an alert into issues' },
        { value: 'improve_eval', label: 'Improve an eval' },
      ] },
      { key: 'question', label: 'Question', type: 'expression', placeholder: 'What changed in error rate since yesterday?', showIf: (c) => c.task !== 'improve_eval' && c.task !== 'investigate_issue' },
      // Investigate: Ask takes the alert that fired, checks what is already
      // filed, reads a bounded sample if it is new, and files or attaches the
      // issue. The step's output carries the issue for a Slack step to link.
      { key: 'sample_cap', label: 'Rows to read at most', type: 'number', placeholder: '40', showIf: (c) => c.task === 'investigate_issue' },
      { key: 'question', label: 'Instructions (optional)', type: 'code', language: 'markdown', showIf: (c) => c.task === 'investigate_issue' },
      // Improve an eval: Ask reads its results, decides whether the eval or the
      // agent is wrong, and records a proposed change in Proposed changes.
      { key: 'eval_lineage_id', label: 'Eval', help: 'Empty uses the eval the alert watches.', type: 'expression', placeholder: '{{ $trigger.alert.eval_lineage_id }}', showIf: (c) => c.task === 'improve_eval' },
      { key: 'question', label: 'Instructions', type: 'code', language: 'markdown', showIf: (c) => c.task === 'improve_eval' },
      { key: 'mode', label: 'Mode', type: 'select', showIf: (c) => c.task !== 'improve_eval' && c.task !== 'investigate_issue', options: [
        { value: 'quick', label: 'Analyse' },
        { value: 'deep', label: 'Extended thinking' },
      ] },
      { key: 'effort', label: 'Effort', type: 'select', showIf: (c) => c.task !== 'improve_eval' && c.task !== 'investigate_issue' && c.mode === 'deep', options: [
        { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'max', label: 'Max' },
      ] },
    ],
    validate: (c) => {
      if (c.task === 'improve_eval' || c.task === 'investigate_issue') return ok;
      if (!need(c.question)) return fail('Write the question to ask.', 'question');
      if (c.mode !== 'quick' && c.mode !== 'deep') return fail('Choose Analyse or Extended thinking.', 'mode');
      return ok;
    },
  },

  // ─── Data ────────────────────────────────────────────────────────────────
  data_query: {
    kind: 'data_query', displayName: 'Query data', category: 'data', icon: Database,
    description: 'Read events, runs, spans or users.',
    defaults: { entity: 'events', event_name: '', filters: { match: 'AND', clauses: [] }, fields: [], limit: 1000, time_window: { kind: 'relative', value: '7d' } },
    configSchema: [
      { key: 'filters', label: 'Filter', type: 'entity_filters', entityField: 'entity', eventNameField: 'event_name' },
      { key: 'time_window', label: 'Time window', type: 'time_window' },
      { key: 'limit', label: 'Limit', type: 'number' },
    ],
  },
  dataset_feed: {
    kind: 'dataset_feed', displayName: 'Find dataset units', category: 'data', icon: Database,
    description: 'Units a Datasets filter matches since the last run.',
    defaults: { kind: 'spans', filter: { modularFilters: [], time: { preset: '7d' } }, since: 'last_run', options: {} },
    configSchema: [
      { key: 'kind', label: 'Units', type: 'select', options: [['spans', 'Spans'], ['runs', 'Runs'], ['conversations', 'Conversations'], ['eval_results', 'Verdicts']].map(([value, label]) => ({ value, label })) },
      { key: 'filter', label: 'Filter', type: 'code', language: 'json', help: 'The same filter as a dataset source. Spans, runs, conversations: { modularFilters, time, eval_conditions }. Verdicts: { lineage_ids, outcomes, time }.' },
      { key: 'since', label: 'Window', type: 'select', options: [{ value: 'last_run', label: 'Since the last run' }, { value: 'window', label: "The filter's own time window" }] },
      { key: 'options', label: 'Options', type: 'code', language: 'json', optional: true, optionalDefault: {}, help: '{ "limit": 500 }. Verdicts, one step per row: { "rows": "verdicts", "node_id": "<step>" }' },
    ],
    validate: (c) => (['spans', 'runs', 'conversations', 'eval_results'].includes(c.kind) ? ok : fail('Choose what to find.', 'kind')),
  },
  dataset_append: {
    kind: 'dataset_append', displayName: 'Add rows to a dataset', category: 'data', icon: Database,
    description: 'Write units or rows into a dataset, one execution each.',
    defaults: { dataset: '', rows: '{{ $json.units }}' },
    configSchema: [
      { key: 'dataset', label: 'Dataset', type: 'dataset_picker', help: 'Pick a dataset, or type a name to create one on the first run.' },
      { key: 'rows', label: 'Rows', type: 'expression', placeholder: '{{ $json.units }}', help: 'Units from Find dataset units ({kind, id}), ids ({span_id} / {run_id} / {conversation_id} / {eval_run_id}), or plain rows ({input, expected_output}).' },
      { key: 'include_trace', label: 'Keep every span of a run', type: 'checkbox' },
    ],
    validate: (c) => (need(c.dataset) ? ok : fail('Choose the dataset.', 'dataset')),
  },
  dataset_rows: {
    kind: 'dataset_rows', displayName: 'Read dataset rows', category: 'data', icon: Database,
    description: 'Rows of a dataset, by Expected or by their eval results.',
    defaults: { dataset: '', rows: 'no_expected', limit: 100 },
    configSchema: [
      { key: 'dataset', label: 'Dataset', type: 'dataset_picker', allowNew: false },
      { key: 'rows', label: 'Rows', type: 'select', options: [
        { value: 'all', label: 'Every row' },
        { value: 'no_expected', label: 'Without Expected' },
        { value: 'expected', label: 'With Expected' },
        { value: 'passed', label: 'Passed their evals' },
        { value: 'failed', label: 'Failed an eval' },
        { value: 'marked_wrong', label: 'A verdict was marked wrong' },
      ] },
      { key: 'eval', label: 'Eval (optional)', type: 'text', placeholder: 'Any eval', help: 'An eval name or id: passed and failed then look at that eval only.', showIf: (c) => ['passed', 'failed', 'marked_wrong'].includes(c.rows) },
      { key: 'min_score', label: 'Score at least (optional)', type: 'number', placeholder: '0.8' },
      { key: 'limit', label: 'Rows at most', type: 'number', placeholder: '100' },
    ],
    validate: (c) => (need(c.dataset) ? ok : fail('Choose the dataset.', 'dataset')),
  },
  dataset_update: {
    kind: 'dataset_update', displayName: 'Update dataset rows', category: 'data', icon: Database,
    description: 'Write Expected, Output or metadata onto rows, by id.',
    defaults: { dataset: '', updates: '{{ $json.updates }}', write_to: 'expected' },
    configSchema: [
      { key: 'dataset', label: 'Dataset', type: 'dataset_picker', allowNew: false },
      { key: 'updates', label: 'Updates', type: 'expression', placeholder: '{{ $json.updates }}', help: 'A list of { id, expected?, output?, metadata? }, or an LLM step\'s for-each results. Each run that changes rows saves one dataset version you can restore.' },
      { key: 'write_to', label: 'An LLM answer goes to', type: 'select', options: [{ value: 'expected', label: 'Expected' }, { value: 'output', label: 'Output' }] },
      { key: 'version_name', label: 'Version name (optional)', type: 'text', placeholder: 'Expected from <agent>' },
    ],
    validate: (c) => (need(c.dataset) ? ok : fail('Choose the dataset.', 'dataset')),
  },
  data_sql: {
    kind: 'data_sql', displayName: 'SQL', category: 'data', icon: Code2,
    description: 'Run a read-only SQL query on your data.',
    defaults: { sql: '-- SELECT only; the team is scoped for you.\nSELECT event_name, COUNT(*)\nFROM events\nGROUP BY event_name\nORDER BY 2 DESC\nLIMIT 50' },
    configSchema: [{ key: 'sql', label: 'SQL', type: 'sql' }],
    validate: (c) => (need(c.sql) ? ok : fail('Write the query.', 'sql')),
  },
  http_request: {
    kind: 'http_request', displayName: 'HTTP request', category: 'data', icon: Send,
    description: 'Call any HTTP endpoint.',
    defaults: { method: 'GET', url: '', headers: {}, body: '' },
    configSchema: [
      { key: 'method', label: 'Method', type: 'select', options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => ({ value: m, label: m })) },
      { key: 'url', label: 'URL', type: 'text', placeholder: 'https://api.example.com/v1/…' },
      { key: 'headers', label: 'Headers', type: 'kv' },
      { key: 'body', label: 'Body', type: 'code', language: 'json' },
    ],
    validate: (c) => (need(c.url) ? ok : fail('Set the URL.', 'url')),
  },
  python_node: {
    kind: 'python_node', displayName: 'Python', category: 'data', icon: Hammer,
    description: 'Run Python in a sandbox.',
    defaults: {
      code: `# numpy, pandas, scipy, requests and the stdlib are available.
import json
import pandas as pd

# \`inputs\` is what the previous step produced. Assign \`outputs\`.
outputs = {"result": inputs}
`,
      requirements: [],
      timeout_seconds: 60,
    },
    configSchema: [
      { key: 'code', label: 'Code', type: 'code', language: 'python' },
      { key: 'requirements', label: 'pip requirements', type: 'lines', optional: true, optionalDefault: [] },
      { key: 'timeout_seconds', label: 'Timeout (seconds)', type: 'number', optional: true, optionalDefault: 60 },
    ],
    validate: (c) => (need(c.code) ? ok : fail('Write the code.', 'code')),
  },

  // ─── Flow ────────────────────────────────────────────────────────────────
  set_fields: {
    kind: 'set_fields', displayName: 'Set fields', category: 'flow', icon: FunctionSquare,
    description: 'Build an object from earlier values.',
    defaults: { assignments: [{ key: 'value', expression: '{{ $input }}' }] },
    configSchema: [{ key: 'assignments', label: 'Fields', type: 'assignments' }],
  },
  filter_node: {
    kind: 'filter_node', displayName: 'Filter', category: 'flow', icon: FilterIcon,
    description: 'Continue only when a condition holds.',
    defaults: { expression: '{{ $input.value > 0 }}' },
    configSchema: [{ key: 'expression', label: 'Condition', type: 'expression' }],
  },
  if_node: {
    kind: 'if_node', displayName: 'If', category: 'flow', icon: GitBranch,
    description: 'Branch on a condition: true or false.',
    ports: ['true', 'false'],
    defaults: { expression: '{{ $input.value > 0 }}' },
    configSchema: [{ key: 'expression', label: 'Condition', type: 'expression' }],
  },
  decide: {
    kind: 'decide', displayName: 'Decide', category: 'flow', icon: Split,
    description: 'Route on a model\'s answers.',
    portsOf: (c) => decidePorts(c),
    portLabel: (c, port) => decidePortLabel(c, port),
    defaults: () => ({
      model: {},
      state: '',
      questions: {},
      routes: { routes: [], otherwise: { name: 'Otherwise' } },
      // On by default, like the TypeSafe console: an unsure answer is "can't tell".
      allow_abstain: true,
      min_confidence: null,
    }),
    // Each of these renders as one collapsible section (fields/DecideFields).
    configSchema: [
      { key: 'model', label: 'Model', type: 'decision_model' },
      { key: 'state', label: 'State', type: 'decision_state' },
      { key: 'questions', label: 'Questions', type: 'decision_questions' },
      { key: 'allow_abstain', label: "Can't tell", type: 'decision_abstain' },
      { key: 'routes', label: 'Routes', type: 'decision_routes' },
    ],
    validate: (c) => {
      if (!need(c.model?.credential_id)) return fail('Choose the decision model.', 'model');
      const qs = c.questions && typeof c.questions === 'object' ? c.questions : {};
      if (!Object.keys(qs).length) return fail('Add a question.', 'questions');
      const qp = Object.values(rowProblems(fromApi(qs)))[0];
      if (qp) return fail(qp[0], 'questions');
      const tp = tableProblems(qs, c.routes || { routes: [], otherwise: null })[0];
      if (tp) return fail(tp.charAt(0).toUpperCase() + tp.slice(1), 'routes');
      return ok;
    },
  },
  loop_node: {
    kind: 'loop_node', displayName: 'Loop', category: 'flow', icon: RefreshCw,
    description: 'Turn a value into a list for the next step.',
    defaults: { items_expression: '{{ $input.rows }}' },
    configSchema: [{ key: 'items_expression', label: 'Items', type: 'expression' }],
  },
  wait_node: {
    kind: 'wait_node', displayName: 'Wait', category: 'flow', icon: Pause,
    description: 'Pause before the next step.',
    defaults: { seconds: 5 },
    configSchema: [{ key: 'seconds', label: 'Seconds', type: 'number' }],
  },
  stop_node: {
    kind: 'stop_node', displayName: 'Stop', category: 'flow', icon: Square,
    description: 'End the run as succeeded or failed.',
    defaults: { status: 'succeeded' },
    configSchema: [{ key: 'status', label: 'End as', type: 'select', options: [{ value: 'succeeded', label: 'Succeeded' }, { value: 'failed', label: 'Failed' }] }],
  },
};

Object.assign(NODE_CATALOG, ALL_INTEGRATION_NODES);

const INTEGRATION_FALLBACK_ICON = { slack: Slack, github: Github, linear: Box };
for (const [kind, spec] of Object.entries(NODE_CATALOG)) {
  if ((spec.category === 'integration' || spec.triggerProvider) && !spec.icon) {
    spec.icon = INTEGRATION_FALLBACK_ICON[kind.split('_')[0]] || Box;
  }
}

/** The node's own defaults, fresh (a function so shared objects are never aliased). */
export function defaultsFor(kind) {
  const d = NODE_CATALOG[kind]?.defaults;
  return typeof d === 'function' ? d() : JSON.parse(JSON.stringify(d || {}));
}

/**
 * Everything blocking a node from running: `[{ field, message }]`, empty when
 * it is ready. `field` is the config key to show the message under (null when
 * it belongs to the node as a whole). A node's own `validate` speaks for it;
 * otherwise every missing `required` field is listed.
 */
export function nodeIssues(node) {
  const spec = NODE_CATALOG[node?.kind];
  if (!spec) return [{ field: null, message: `"${node?.kind}" is not a node this agent can run.` }];
  const cfg = node?.config || {};
  if (typeof spec.validate === 'function') {
    const r = spec.validate(cfg, node) || ok;
    return r.ok ? [] : [{ field: r.field || null, message: r.message }];
  }
  return (spec.configSchema || [])
    .filter((f) => f.required && (typeof f.showIf !== 'function' || f.showIf(cfg)) && !need(cfg[f.key]))
    .map((f) => ({ field: f.key, message: `${f.label} is required.` }));
}

/**
 * Is this node ready to run? `{ ok, message, field }`. The canvas flags a node
 * that is not; the server refuses to publish a graph with one.
 */
export function validateNode(node) {
  const spec = NODE_CATALOG[node?.kind];
  if (!spec) return fail(`"${node?.kind}" is not a node this agent can run.`);
  const cfg = node?.config || {};
  if (typeof spec.validate === 'function') return spec.validate(cfg, node) || ok;
  for (const f of spec.configSchema || []) {
    if (f.required && !need(cfg[f.key])) return fail(`${f.label} is required.`, f.key);
  }
  return ok;
}

/**
 * The ports a node leaves by, or null when it has one plain way out. Twin of
 * the server's definition.portsOf: an edge out of a node with ports must
 * name one of them.
 */
export function portsOf(node) {
  const spec = NODE_CATALOG[node?.kind];
  if (!spec) return null;
  if (typeof spec.portsOf === 'function') return spec.portsOf(node?.config || {});
  return spec.ports || null;
}

/** What a port is called where people read it: a route's name, else the port id. */
export function portLabel(node, port) {
  const spec = NODE_CATALOG[node?.kind];
  if (spec && typeof spec.portLabel === 'function') return spec.portLabel(node?.config || {}, port);
  return port == null ? '' : String(port);
}

// Node appearance (tile style, summaries) lives in nodeAppearance.js; these
// names are re-exported so existing importers keep working.
export { accentFor, accentTint, accentEdge, SECTION_ACCENT } from 'features/agents/WorkflowEditor/nodeAppearance';

const PROVIDER_LABEL = { slack: 'Slack', github: 'GitHub', linear: 'Linear' };

/**
 * The palette. Triggers on their own (offered only while the graph has none),
 * then AI, apps, data, flow.
 */
export function paletteGrouped() {
  const all = Object.values(NODE_CATALOG);
  const triggers = all.filter((n) => n.category === 'trigger');
  const integrations = all.filter((n) => n.category === 'integration');
  const byProvider = (items) => {
    const by = {};
    for (const n of items) {
      const p = n.provider || n.triggerProvider;
      if (!PALETTE_PROVIDERS.includes(p)) continue;
      (by[p] ||= []).push(n);
    }
    return Object.entries(by).map(([provider, list]) => ({
      provider,
      label: PROVIDER_LABEL[provider] || provider,
      items: list,
      actionGroups: actionGroupsFor(provider, list),
    }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };
  return [
    { id: 'triggers', label: 'Triggers', items: triggers.filter((t) => !t.triggerProvider), providerGroups: byProvider(triggers.filter((t) => t.triggerProvider)) },
    { id: 'ai', label: 'AI', items: all.filter((n) => n.category === 'ai') },
    { id: 'actions', label: 'Apps', providerGroups: byProvider(integrations.filter((n) => !n.triggerProvider)) },
    { id: 'data', label: 'Data', items: all.filter((n) => n.category === 'data') },
    { id: 'flow', label: 'Flow', items: all.filter((n) => n.category === 'flow') },
  ];
}
